/**
 * CSS selector for commonly focusable elements.
 * Matches links with href, buttons, and elements with non-negative tabindex.
 */
export const FOCUSABLE_SELECTOR =
  'a[href], button, [tabindex]:not([tabindex="-1"])';

/**
 * Defers focusing an element until after the current event loop.
 * This ensures focus happens after React's current render cycle completes.
 *
 * @param element - The element to focus, or null/undefined to do nothing
 *
 * @example
 * ```tsx
 * const handleClose = () => {
 *   closeMenu();
 *   deferFocus(triggerRef.current);
 * };
 * ```
 */
export function deferFocus(element: HTMLElement | null | undefined): void {
  setTimeout(() => element?.focus(), 0);
}

/**
 * Calculates the next index for circular navigation through a list.
 * Wraps around at boundaries (last -> first, first -> last).
 *
 * @param currentIndex - The current position in the list
 * @param length - The total number of items in the list
 * @param direction - 'next' to move forward, 'prev' to move backward
 * @returns The next index after circular navigation
 *
 * @example
 * ```tsx
 * const handleArrowKey = (e: KeyboardEvent) => {
 *   const direction = e.key === 'ArrowDown' ? 'next' : 'prev';
 *   const nextIdx = getNextIndex(currentIdx, items.length, direction);
 *   items[nextIdx].focus();
 * };
 * ```
 */
export function getNextIndex(
  currentIndex: number,
  length: number,
  direction: 'next' | 'prev',
): number {
  if (length === 0) return 0;
  if (direction === 'next') {
    return (currentIndex + 1) % length;
  }
  return currentIndex === 0 ? length - 1 : currentIndex - 1;
}

/**
 * Creates a keyboard event handler that triggers an action when Enter or Space is pressed.
 * Common pattern for making non-button elements keyboard accessible.
 *
 * @param action - The callback to execute when an activation key is pressed
 * @returns A keyboard event handler
 *
 * @example
 * ```tsx
 * <div
 *   role="button"
 *   tabIndex={0}
 *   onClick={handleClick}
 *   onKeyDown={handleActivationKeyDown(handleClick)}
 * >
 *   Clickable content
 * </div>
 * ```
 */
export function handleActivationKeyDown<T extends { key: string }>(
  action: () => void,
): (e: T) => void {
  return (e: T) => {
    if (e.key === 'Enter' || e.key === ' ') {
      // Prevent default only for keyboard events that have preventDefault
      if ('preventDefault' in e && typeof e.preventDefault === 'function') {
        (e as unknown as { preventDefault: () => void }).preventDefault();
      }
      action();
    }
  };
}

/**
 * Creates a keyboard event handler that triggers an action when Escape is pressed.
 * Common pattern for closing modals, menus, or canceling actions.
 *
 * @param action - The callback to execute when Escape is pressed
 * @returns A keyboard event handler
 *
 * @example
 * ```tsx
 * <div
 *   onKeyDown={handleEscapeKeyDown(handleClose)}
 * >
 *   Closable content
 * </div>
 * ```
 */
export function handleEscapeKeyDown<T extends { key: string }>(
  action: () => void,
): (e: T) => void {
  return (e: T) => {
    if (e.key === 'Escape') {
      // Prevent default only for keyboard events that have preventDefault
      if ('preventDefault' in e && typeof e.preventDefault === 'function') {
        (e as unknown as { preventDefault: () => void }).preventDefault();
      }
      action();
    }
  };
}
